import React from 'react';
import { Col, Row, Upload, UploadFile, UploadProps } from 'antd';
import ImgCrop from 'antd-img-crop';
import type { UploadRequestOption } from 'rc-upload/lib/interface';
import { FileGrid } from './FileGrid';
import axios from 'axios';
import { IPlanPhoto } from '../../types/plans';

interface UploadImageProps extends Omit<UploadProps, 'onChange'> {
  maxCount?: number;
  aspect?: number;
  crop?: boolean;
  value?: (IPlanPhoto & UploadFile)[];
  onChange?: (files: UploadFile[]) => void;
  folder?: string;
  addWatermark?: boolean;
  getActions?: (file: UploadFile, onChange?: (file: UploadFile) => void) => React.ReactNode[];
}

export const UploadImage: React.FC<UploadImageProps> = ({
  maxCount = 1,
  aspect = 1,
  crop = false,
  value = [],
  onChange,
  folder = 'images',
  addWatermark = false,
  getActions,
  ...props
}) => {
  const handleChange: UploadProps['onChange'] = ({ fileList }) => {
    const updatedFiles = fileList.map(file => {
      const data: { url?: string; size?: number; type?: string } = {};
      if (file.response && file.status === 'done') {
        if (file.response.original) {
          const originalFile = file.response.original;
          data.url = originalFile.url;
          data.size = originalFile.size;
          data.type = originalFile.type;
        }
      }
      const response = {
        ...file,
        url: file.url || data.url,
        ...data,
        ...file.response,
      };
      return response;
    });
    onChange?.(updatedFiles);
  };

  const customUpload = async (options: UploadRequestOption) => {
    const formData = new FormData();
    formData.append('image', options.file);
    formData.append('folder', folder);
    formData.append('addWatermark', addWatermark.toString());

    try {
      const { data } = await axios.post('/api/upload/image', formData);
      if (data.status === 'success') {
        options.onSuccess?.(data.data);
      } else {
        options.onError?.(new Error(data.message));
      }
    } catch (error) {
      options.onError?.(error as Error);
    }
  };

  const uploadButton = (
    <div>
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const UploadComponent = (
    <Upload
      listType="picture-card"
      fileList={value}
      onChange={handleChange}
      customRequest={customUpload}
      showUploadList={false}
      {...props}>
      {value.length >= maxCount ? null : uploadButton}
    </Upload>
  );

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <FileGrid
          fileList={value}
          onRemove={(file: UploadFile) => {
            const newFileList = value.filter(item => item.uid !== file.uid);
            onChange?.(newFileList);
          }}
          getActions={getActions}
          onChange={(file: UploadFile) => {
            const newFileList = value.map(item => (item.uid === file.uid ? file : item));
            onChange?.(newFileList);
          }}
          hideMetadata={false}
        />
      </Col>
      <Col span={24}>
        {crop ? (
          <ImgCrop aspect={aspect} quality={1} modalTitle="Edit Image">
            {UploadComponent}
          </ImgCrop>
        ) : (
          UploadComponent
        )}
      </Col>
    </Row>
  );
};
